/**
 * Turning a chart on screen into a controlled document.
 *
 * The org chart and the department chart are the same kind of object — an
 * absolutely-positioned tree inside a scrolling viewport — and they have to
 * come out of the printer looking like the same document: the same header, the
 * same HR-F-24 control box, the same sign-off line. This module is where that
 * lives, so changing the doc number or the footer changes both sheets rather
 * than one of them.
 *
 * Two steps, kept separate because Print and PDF share the first and differ in
 * the second: `captureChart` snapshots the chart at natural size, and
 * `chartCanvasToPdf` lays that snapshot onto a page.
 */

// Controlled-document identity for every printed chart, and the sign-off pair
// that closes it. Declared once so the print sheet and the PDF export can never
// drift apart — change a value and both outputs follow.
export const DOC_CONTROL = [
  ['Doc. No:', 'HR-F-24'],
  ['Rev. No:', '00'],
  ['Rev.Date:', '17/04/2023'],
];
export const SIGN_OFF = { prepared: 'Anju Ravish', approved: 'Sanjeev Gumber' };

// Scoped export-only CSS: fixes three html2canvas quirks (level-pill glyph
// clipping from sub-pixel width rounding on tiny inline-block text, the
// CORPORATE badge overlapping the designation because align-middle between
// differently-sized fonts renders differently once the zoom transform is off,
// and stray card shadows). Applies ONLY while `.pdf-exporting` is on the root.
const EXPORT_CSS = `
  /* html2canvas mis-wraps very tight inline-block "L##" pills, pushing the
     trailing digit onto a second line that falls outside the pill's box.
     Force block layout with a fixed pill width so the whole "L##" always
     paints on one line, centred under the card. */
  .pdf-exporting [data-orgchart-level-pill] {
    display: block !important;
    width: 42px !important;
    min-width: 42px !important;
    margin: 4px auto 0 !important;
    padding: 2px 0 !important;
    font-size: 11px !important;
    line-height: 14px !important;
    letter-spacing: 0 !important;
    text-align: center !important;
    overflow: visible !important;
    white-space: nowrap !important;
    word-break: keep-all !important;
    box-sizing: border-box !important;
    border: 1px solid rgba(17, 24, 43, 0.10) !important;
    vertical-align: middle !important;
  }
  /* Force the CORPORATE badge onto its own line, centered under the
     designation, with a hug-content width. display:table achieves both
     block-flow break + shrink-to-fit width on an inline <span>. */
  .pdf-exporting [data-orgchart-corp-badge] {
    display: table !important;
    margin: 4px auto 0 !important;
    padding: 2px 8px !important;
    font-size: 9px !important;
    line-height: 12px !important;
    letter-spacing: 0.04em !important;
    vertical-align: baseline !important;
    white-space: nowrap !important;
  }
  .pdf-exporting [data-orgchart-corp-badge] + * {
    margin-top: 2px !important;
  }
  /* html2canvas paints every box-shadow as a detached blurred rectangle and
     mis-places the ones belonging to absolutely-positioned cards, which
     litters the empty parts of the sheet with grey ghost boxes. The cards
     already read as cards from their fill + border, so drop shadows for the
     duration of the capture. */
  .pdf-exporting,
  .pdf-exporting * {
    box-shadow: none !important;
    text-shadow: none !important;
    filter: none !important;
  }
`;

/**
 * Snapshot a chart to a canvas at its natural size — the whole tree, not the
 * part that happens to be on screen, and without the zoom transform.
 *
 * `content` is the transformed layer the cards live on, `wrapper` its sized
 * parent, `scrollView` the element that scrolls. All three are restored
 * exactly as they were, on success or failure.
 */
export async function captureChart({ content, wrapper, scrollView, width, height, label = 'Chart' }) {
  if (!content || !wrapper) throw new Error('Chart is not ready yet');

  // Snapshot everything we're about to mutate so we can restore exactly.
  const orig = {
    transform: content.style.transform,
    wrapperW: wrapper.style.width,
    wrapperH: wrapper.style.height,
    scrollL: scrollView?.scrollLeft ?? 0,
    scrollT: scrollView?.scrollTop ?? 0,
    viewOverflow: scrollView?.style.overflow || '',
  };

  // A little bottom padding so the last row's variable-height cards (name +
  // designation + department + level pill) are never clipped.
  const naturalW = Math.ceil(width);
  const naturalH = Math.ceil(height) + 24;

  const styleEl = document.createElement('style');
  styleEl.setAttribute('data-pdf-export-style', '1');
  styleEl.textContent = EXPORT_CSS;
  try {
    const html2canvas = (await import('html2canvas')).default;

    // Render at natural size (no zoom, no scroll clipping)
    content.style.transform = 'none';
    wrapper.style.width = `${naturalW}px`;
    wrapper.style.height = `${naturalH}px`;
    if (scrollView) {
      scrollView.style.overflow = 'visible';
      scrollView.scrollLeft = 0;
      scrollView.scrollTop = 0;
    }
    document.head.appendChild(styleEl);
    content.classList.add('pdf-exporting');
    // Let the browser reflow before snapshotting. The timeout is a floor, not
    // a nicety: a tab that loses focus mid-export gets its rAF throttled, and
    // without it the capture would wait forever.
    await new Promise((r) => {
      const t = setTimeout(r, 400);
      requestAnimationFrame(() => requestAnimationFrame(() => { clearTimeout(t); r(); }));
    });

    const scale = Math.min(3, Math.max(2, Math.floor(4000 / Math.max(naturalW, naturalH)) || 2));
    const canvas = await html2canvas(content, {
      backgroundColor: '#ffffff',
      scale,
      width: naturalW,
      height: naturalH,
      windowWidth: naturalW,
      windowHeight: naturalH,
      useCORS: true,
      logging: false,
      letterRendering: true,
      imageTimeout: 15000,
    });

    // Log dimensions for verification (visible in browser console)
    console.log(`[${label} PDF] natural =`, { w: naturalW, h: naturalH },
                ' captured =', { w: canvas.width, h: canvas.height }, ' scale =', scale);
    return canvas;
  } finally {
    // Always restore the UI, even on error
    content.classList.remove('pdf-exporting');
    if (styleEl.parentNode) styleEl.parentNode.removeChild(styleEl);
    content.style.transform = orig.transform;
    wrapper.style.width = orig.wrapperW;
    wrapper.style.height = orig.wrapperH;
    if (scrollView) {
      scrollView.style.overflow = orig.viewOverflow;
      scrollView.scrollLeft = orig.scrollL;
      scrollView.scrollTop = orig.scrollT;
    }
  }
}

/**
 * Lay a captured chart onto a PDF page and save it.
 *
 * The page is sized to the chart's own aspect ratio so the whole thing fits on
 * ONE legible page, with a fixed-height band above it for the title and the
 * controlled-document box, and a fixed-height band below it for the sign-off.
 */
export async function chartCanvasToPdf(canvas, title) {
  const jsPDF = (await import('jspdf')).default;

  const cw = canvas.width, ch = canvas.height;
  const landscape = cw >= ch;

  // Long edge capped at 1600pt so the file stays reasonable but crisp.
  const marginPt = 28;
  const headerPt = 62;   // title + doc-control box
  const footerPt = 40;   // Prepared By / Approved By
  const maxEdgePt = 1600;
  let usableW, usableH;
  if (landscape) {
    usableW = maxEdgePt;
    usableH = Math.round((ch / cw) * maxEdgePt);
  } else {
    usableH = maxEdgePt;
    usableW = Math.round((cw / ch) * maxEdgePt);
  }
  const pageW = usableW + marginPt * 2;
  const pageH = usableH + marginPt * 2 + headerPt + footerPt;

  const pdf = new jsPDF({
    orientation: landscape ? 'landscape' : 'portrait',
    unit: 'pt',
    format: [pageW, pageH],
    compress: true,
  });

  // ---- Controlled-document box, pinned to the top-right corner -------------
  // Drawn as vector text at a fixed size and offset from the right margin, so
  // it lands in exactly the same place on every sheet no matter how wide or
  // tall the chart underneath happens to be.
  const boxW = 176, rowH = 17, boxH = rowH * DOC_CONTROL.length;
  const boxX = pageW - marginPt - boxW, boxY = marginPt;
  pdf.setDrawColor(17, 24, 43);
  pdf.setLineWidth(0.8);
  pdf.rect(boxX, boxY, boxW, boxH);
  pdf.setFontSize(8.5);
  DOC_CONTROL.forEach(([label, value], i) => {
    const y = boxY + rowH * i;
    if (i > 0) pdf.line(boxX, y, boxX + boxW, y);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(17, 24, 43);
    pdf.text(label, boxX + 7, y + 11.5);
    pdf.setFont('helvetica', 'normal');
    pdf.text(String(value), boxX + 62, y + 11.5);
  });

  // ---- Title block, left of the box ---------------------------------------
  pdf.setFont('helvetica', 'bold');
  pdf.setFontSize(16);
  pdf.setTextColor(17, 24, 43);
  pdf.text(title, marginPt, marginPt + 16, { maxWidth: boxX - marginPt - 20 });
  pdf.setFont('helvetica', 'normal');
  pdf.setFontSize(9);
  pdf.setTextColor(90, 99, 119);
  pdf.text(`Generated ${new Date().toLocaleDateString()}`, marginPt, marginPt + 32);

  // ---- Chart ---------------------------------------------------------------
  pdf.addImage(
    canvas.toDataURL('image/png'),
    'PNG',
    marginPt,
    marginPt + headerPt,
    usableW,
    usableH,
    undefined,
    'FAST',
  );

  // ---- Sign-off footer -----------------------------------------------------
  const footY = marginPt + headerPt + usableH + 22;
  pdf.setDrawColor(214, 219, 230);
  pdf.setLineWidth(0.6);
  pdf.line(marginPt, footY - 12, pageW - marginPt, footY - 12);
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(17, 24, 43);
  pdf.text('Prepared By:', marginPt, footY);
  pdf.setFont('helvetica', 'normal');
  pdf.text(SIGN_OFF.prepared, marginPt + 66, footY);
  pdf.setFont('helvetica', 'bold');
  const approvedLabelW = pdf.getTextWidth('Approved By:');
  const approvedValueW = pdf.getTextWidth(SIGN_OFF.approved) + 8;
  const approvedX = pageW - marginPt - approvedLabelW - approvedValueW;
  pdf.text('Approved By:', approvedX, footY);
  pdf.setFont('helvetica', 'normal');
  pdf.text(SIGN_OFF.approved, approvedX + approvedLabelW + 8, footY);

  const safe = title.replace(/[^A-Za-z0-9\-_ ]+/g, '').replace(/\s+/g, '-');
  pdf.save(`${safe}.pdf`);
}
